import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Logo } from '../components/Logo';
import PageTitle from '../components/PageTitle';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { 
  ArrowRight, 
  ArrowLeft,
  Check, 
  ChevronRight,
  ShoppingBag,
  Truck,
  Shield,
  CreditCard,
  Star,
  Play,
  Minus,
  Plus,
  Loader2,
  LogOut,
  User,
  Settings,
  Gift
} from 'lucide-react';
import { toast } from 'sonner';

// Machine data
const machineData = {
  micro: {
    id: "micro",
    name: "Lagree Micro",
    tagline: "The Home Studio Essential",
    description: "The Lagree Micro brings the full Megaformer experience to your home in a compact, space-efficient design. Perfect for dedicated home studios or living spaces, the Micro delivers the same slow, controlled movements and Time Under Tension training as studio machines.",
    price: 4990,
    monthlyPrice: 167,
    images: [
      "https://images.unsplash.com/photo-1638805981949-362f5964521e?q=85&w=1200&auto=format&fit=crop",
      "https://images.unsplash.com/photo-1571019614242-c5c5dee9f50b?q=85&w=1200&auto=format&fit=crop",
      "https://images.unsplash.com/photo-1518611012118-696072aa579a?q=85&w=1200&auto=format&fit=crop"
    ],
    specs: {
      dimensions: '84" L x 24" W x 10" H',
      weight: "165 lbs",
      maxUserWeight: "300 lbs",
      springs: "4 color-coded springs (Yellow, Red, Blue, Black)",
      carriage: "Full-range padded carriage with handlebar grips"
    },
    features: [
      "Compact footprint perfect for home studios",
      "Full Megaformer functionality",
      "4 variable-resistance springs",
      "Smooth-gliding carriage system",
      "Padded platform for comfort",
      "Easy storage with fold-up design",
      "Includes starter spring set",
      "Compatible with all Lagree classes"
    ],
    whatsIncluded: [
      "Lagree Micro machine",
      "4 color-coded springs (starter set)",
      "Padded handlebar grips",
      "Setup guide & video tutorial",
      "1-year manufacturer warranty"
    ],
    bestseller: true,
    reviews: { rating: 4.9, count: 847 }
  },
  "mini-pro": {
    id: "mini-pro",
    name: "Lagree Mini Pro",
    tagline: "Professional Performance",
    description: "The Mini Pro is our premium home machine, featuring an enhanced spring system and extended carriage range for the most demanding workouts. Built with studio-grade components, it's the choice for serious Lagree practitioners.",
    price: 6490,
    monthlyPrice: 217,
    images: [
      "https://images.unsplash.com/photo-1571019614242-c5c5dee9f50b?q=85&w=1200&auto=format&fit=crop",
      "https://images.unsplash.com/photo-1518611012118-696072aa579a?q=85&w=1200&auto=format&fit=crop",
      "https://images.unsplash.com/photo-1638805981949-362f5964521e?q=85&w=1200&auto=format&fit=crop"
    ],
    specs: {
      dimensions: '96" L x 26" W x 12" H',
      weight: "195 lbs",
      maxUserWeight: "350 lbs",
      springs: "6 color-coded springs (Yellow, Red, Blue, Black, Green, Silver)",
      carriage: "Extended-range professional carriage with premium grips"
    },
    features: [
      "Studio-grade construction",
      "Enhanced 6-spring system for more resistance options",
      "Extended carriage travel range",
      "Premium padded platform",
      "Commercial-quality components",
      "Professional handlebar system",
      "Includes complete spring set",
      "Built for high-frequency use"
    ],
    whatsIncluded: [
      "Lagree Mini Pro machine",
      "6 color-coded springs (complete set)",
      "Premium handlebar grips",
      "Professional setup guide",
      "2-year manufacturer warranty",
      "White glove delivery available"
    ],
    bestseller: false,
    reviews: { rating: 4.8, count: 423 }
  }
};

const ShopPage = () => {
  const { productId } = useParams();
  const navigate = useNavigate();
  const { api, isAuthenticated, user, logout } = useAuth();
  const [selectedImage, setSelectedImage] = useState(0);
  const [quantity, setQuantity] = useState(1);
  const [cartCount, setCartCount] = useState(0);
  const [addingToCart, setAddingToCart] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  // Fetch cart count on mount
  useEffect(() => {
    const fetchCartCount = async () => {
      if (isAuthenticated) {
        try {
          const response = await api.get('/cart/');
          const count = response.data.items?.reduce((sum, item) => sum + item.quantity, 0) || 0;
          setCartCount(count);
        } catch (error) {
          console.error('Failed to fetch cart:', error);
        }
      }
    };
    fetchCartCount();
  }, [isAuthenticated, api]);

  const handleAddToCart = async (machine) => {
    if (!isAuthenticated) {
      toast.error('Please sign in to add items to cart');
      navigate('/login');
      return;
    }

    setAddingToCart(true);
    try {
      await api.post('/cart/add', {
        product_id: machine.id,
        product_type: 'equipment',
        quantity: quantity
      });
      setCartCount(prev => prev + quantity);
      toast.success(`${machine.name} added to cart!`);
    } catch (error) {
      console.error('Failed to add to cart:', error);
      toast.error('Failed to add to cart. Please try again.');
    } finally {
      setAddingToCart(false);
    }
  };

  // If we have a productId, show product detail page
  if (productId && machineData[productId]) {
    const machine = machineData[productId];

    return (
      <div className="min-h-screen bg-[#090D0F]">
        <PageTitle title={`${machine.name}`} description={`Shop the ${machine.name}. ${machine.tagline}`} />
        {/* Header */}
        <header className="sticky top-0 z-50 glass border-b border-zinc-800">
          <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
            <Logo linkTo="/" size="default" />
            <div className="flex items-center gap-4">
              <Link to="/shop">
                <Button variant="ghost" className="text-zinc-400 hover:text-white">
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back to Shop
                </Button>
              </Link>
              <Link to="/cart">
                <Button variant="outline" className="border-zinc-700" data-testid="cart-button">
                  <ShoppingBag className="w-4 h-4 mr-2" />
                  Cart ({cartCount})
                </Button>
              </Link>
            </div>
          </div>
        </header>

        <main className="max-w-7xl mx-auto px-6 py-12">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Image Gallery */}
            <div>
              <div className="aspect-square bg-[#121416] rounded-sm overflow-hidden mb-4">
                <img
                  src={machine.images[selectedImage]}
                  alt={machine.name}
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="flex gap-4">
                {machine.images.map((img, idx) => (
                  <button
                    key={idx}
                    onClick={() => setSelectedImage(idx)}
                    className={`w-24 h-24 rounded-sm overflow-hidden border-2 transition-colors ${
                      selectedImage === idx ? 'border-teal-400' : 'border-zinc-800'
                    }`}
                  >
                    <img src={img} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            </div>

            {/* Product Info */}
            <div>
              {machine.bestseller && (
                <span className="inline-block px-3 py-1 bg-teal-400 text-black text-xs font-bold uppercase rounded-sm mb-4">
                  Bestseller
                </span>
              )}
              <h1 className="text-4xl font-bold mb-2">{machine.name}</h1>
              <p className="text-teal-400 text-lg mb-4">{machine.tagline}</p>

              {/* Rating */}
              <div className="flex items-center gap-2 mb-6">
                <div className="flex">
                  {[...Array(5)].map((_, i) => (
                    <Star
                      key={i}
                      className={`w-5 h-5 ${i < Math.floor(machine.reviews.rating) ? 'text-yellow-500 fill-yellow-500' : 'text-zinc-700'}`}
                    />
                  ))}
                </div>
                <span className="text-zinc-400">{machine.reviews.rating} ({machine.reviews.count} reviews)</span>
              </div>

              {/* Price */}
              <div className="bg-[#121416] border border-zinc-800 rounded-sm p-6 mb-6">
                <div className="flex items-baseline gap-2 mb-2">
                  <span className="text-4xl font-bold">${machine.price.toLocaleString()}</span>
                </div>
                <p className="text-zinc-400 mb-4">
                  or <span className="text-teal-400 font-semibold">${machine.monthlyPrice}/mo</span> with Affirm
                </p>
                <div className="flex items-center gap-4 mb-6">
                  <div className="flex items-center border border-zinc-700 rounded-sm">
                    <button
                      onClick={() => setQuantity(Math.max(1, quantity - 1))}
                      className="p-3 hover:bg-zinc-800 transition-colors"
                    >
                      <Minus className="w-4 h-4" />
                    </button>
                    <span className="px-4 font-medium">{quantity}</span>
                    <button
                      onClick={() => setQuantity(quantity + 1)}
                      className="p-3 hover:bg-zinc-800 transition-colors"
                    >
                      <Plus className="w-4 h-4" />
                    </button>
                  </div>
                  <Button
                    onClick={() => handleAddToCart(machine)}
                    disabled={addingToCart}
                    className="flex-1 h-12 bg-teal-400 text-black font-bold uppercase tracking-wider hover:bg-teal-300 disabled:opacity-50"
                    data-testid="add-to-cart"
                  >
                    {addingToCart ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : (
                      <>
                        <ShoppingBag className="w-5 h-5 mr-2" />
                        Add to Cart
                      </>
                    )}
                  </Button>
                </div>
                <div className="space-y-3 text-sm">
                  <div className="flex items-center gap-3 text-zinc-400">
                    <Truck className="w-5 h-5 text-teal-400" />
                    <span>Free shipping • Delivers in 5-7 business days</span>
                  </div>
                  <div className="flex items-center gap-3 text-zinc-400">
                    <Shield className="w-5 h-5 text-teal-400" />
                    <span>{machine.id === 'mini-pro' ? '2-year' : '1-year'} manufacturer warranty</span>
                  </div>
                  <div className="flex items-center gap-3 text-zinc-400">
                    <CreditCard className="w-5 h-5 text-teal-400" />
                    <span>Financing available through Affirm</span>
                  </div>
                </div>
              </div>

              {/* Description */}
              <p className="text-zinc-400 mb-8">{machine.description}</p>

              {/* Features */}
              <div className="mb-8">
                <h3 className="text-xl font-bold mb-4">Key Features</h3>
                <div className="grid grid-cols-2 gap-3">
                  {machine.features.map((feature, idx) => (
                    <div key={idx} className="flex items-start gap-2">
                      <Check className="w-4 h-4 text-teal-400 mt-1 flex-shrink-0" />
                      <span className="text-zinc-400 text-sm">{feature}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Specs & What's Included */}
          <div className="grid md:grid-cols-2 gap-8 mt-16">
            <div className="bg-[#121416] border border-zinc-800 rounded-sm p-8">
              <h3 className="text-xl font-bold mb-6">Specifications</h3>
              <dl className="space-y-4">
                {Object.entries(machine.specs).map(([key, value]) => (
                  <div key={key} className="flex justify-between border-b border-zinc-800 pb-3">
                    <dt className="text-zinc-500 capitalize">{key.replace(/([A-Z])/g, ' $1').trim()}</dt>
                    <dd className="text-white font-medium">{value}</dd>
                  </div>
                ))}
              </dl>
            </div>

            <div className="bg-[#121416] border border-zinc-800 rounded-sm p-8">
              <h3 className="text-xl font-bold mb-6">What's Included</h3>
              <ul className="space-y-3">
                {machine.whatsIncluded.map((item, idx) => (
                  <li key={idx} className="flex items-center gap-3">
                    <Check className="w-5 h-5 text-teal-400 flex-shrink-0" />
                    <span className="text-zinc-300">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* CTA */}
          <div className="mt-16 text-center bg-teal-400/10 border border-teal-400/30 rounded-sm p-12">
            <h3 className="text-2xl font-bold mb-4">Complete Your Home Studio</h3>
            <p className="text-zinc-400 mb-6 max-w-xl mx-auto">
              Get unlimited access to live and on-demand Lagree classes taught by certified instructors.
            </p>
            <Link to="/register">
              <Button className="h-12 px-8 bg-teal-400 text-black font-bold uppercase tracking-wider hover:bg-teal-300">
                Start 7-Day Free Trial
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </Link>
          </div>
        </main>
      </div>
    );
  }

  // Shop listing page
  const machines = Object.values(machineData);

  return (
    <div className="min-h-screen bg-[#090D0F]">
      <PageTitle title="Shop Lagree Machines" description="Shop premium Lagree Megaformer machines for your home studio." />
      {/* Header */}
      <header className="sticky top-0 z-50 glass border-b border-zinc-800">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Logo linkTo={isAuthenticated ? "/dashboard" : "/"} size="default" />
          
          {isAuthenticated ? (
            <>
              <nav className="hidden md:flex items-center gap-6">
                <Link to="/dashboard" className="text-zinc-400 hover:text-white transition-colors">Home</Link>
                <Link to="/classes" className="text-zinc-400 hover:text-white transition-colors">Classes</Link>
                <Link to="/programs" className="text-zinc-400 hover:text-white transition-colors">Programs</Link>
                <Link to="/shop" className="text-white font-medium">Shop</Link>
                <Link to="/instructors" className="text-zinc-400 hover:text-white transition-colors">Instructors</Link>
              </nav>
              <div className="flex items-center gap-3">
                <Link to="/cart">
                  <Button variant="outline" className="border-zinc-700" data-testid="cart-button-list">
                    <ShoppingBag className="w-4 h-4 mr-2" />
                    Cart ({cartCount})
                  </Button>
                </Link>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" className="flex items-center gap-2" data-testid="user-menu">
                      <div className="w-8 h-8 bg-teal-400 rounded-full flex items-center justify-center text-sm font-bold text-black">
                        {user?.first_name?.charAt(0)}{user?.last_name?.charAt(0)}
                      </div>
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-56 bg-[#121212] border-zinc-800">
                    <DropdownMenuItem asChild>
                      <Link to="/profile" className="flex items-center gap-2 cursor-pointer">
                        <User className="w-4 h-4" />
                        Profile
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <Link to="/referrals" className="flex items-center gap-2 cursor-pointer text-teal-400">
                        <Gift className="w-4 h-4" />
                        Refer & Earn $50
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <Link to="/settings" className="flex items-center gap-2 cursor-pointer">
                        <Settings className="w-4 h-4" />
                        Settings
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuSeparator className="bg-zinc-800" />
                    <DropdownMenuItem 
                      onClick={handleLogout}
                      className="flex items-center gap-2 text-red-400 cursor-pointer"
                    >
                      <LogOut className="w-4 h-4" />
                      Sign Out
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </>
          ) : (
            <div className="flex items-center gap-4">
              <Link to="/login">
                <Button variant="ghost" className="text-zinc-400 hover:text-white">
                  Sign In
                </Button>
              </Link>
              <Link to="/cart">
                <Button variant="outline" className="border-zinc-700" data-testid="cart-button-list">
                  <ShoppingBag className="w-4 h-4 mr-2" />
                  Cart ({cartCount})
                </Button>
              </Link>
            </div>
          )}
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-12">
        {/* Hero */}
        <div className="text-center mb-16">
          <h1 className="text-4xl md:text-6xl font-bold mb-4">SHOP LAGREE MACHINES</h1>
          <p className="text-zinc-500 text-lg max-w-2xl mx-auto">
            Bring the authentic Megaformer experience home. Each machine is engineered for the signature slow, controlled movements of the Lagree Method.
          </p>
        </div>

        {/* Machines Grid */}
        <div className="grid md:grid-cols-2 gap-8 mb-16">
          {machines.map((machine) => (
            <div
              key={machine.id}
              className={`relative bg-[#121416] border rounded-sm overflow-hidden transition-all hover:border-teal-400/50 ${
                machine.bestseller ? 'border-teal-400' : 'border-zinc-800'
              }`}
            >
              {machine.bestseller && (
                <span className="absolute top-4 right-4 px-3 py-1 bg-teal-400 text-black text-xs font-bold uppercase rounded-sm z-10">
                  Bestseller
                </span>
              )}
              <Link to={`/shop/${machine.id}`}>
                <div className="aspect-video relative overflow-hidden">
                  <img
                    src={machine.images[0]}
                    alt={machine.name}
                    className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                  />
                </div>
              </Link>
              <div className="p-8">
                <Link to={`/shop/${machine.id}`}>
                  <h2 className="text-2xl font-bold mb-1 hover:text-teal-400 transition-colors">{machine.name}</h2>
                </Link>
                <p className="text-teal-400 text-sm mb-4">{machine.tagline}</p>
                
                <div className="flex items-center gap-2 mb-4">
                  <div className="flex">
                    {[...Array(5)].map((_, i) => (
                      <Star
                        key={i}
                        className={`w-4 h-4 ${i < Math.floor(machine.reviews.rating) ? 'text-yellow-500 fill-yellow-500' : 'text-zinc-700'}`}
                      />
                    ))}
                  </div>
                  <span className="text-zinc-500 text-sm">({machine.reviews.count})</span>
                </div>

                <div className="flex items-baseline gap-2 mb-6">
                  <span className="text-3xl font-bold">${machine.price.toLocaleString()}</span>
                  <span className="text-zinc-500">or from</span>
                  <span className="text-teal-400 font-semibold">${machine.monthlyPrice}/mo</span>
                </div>

                <ul className="space-y-2 mb-8">
                  {machine.features.slice(0, 4).map((feature, i) => (
                    <li key={i} className="flex items-center gap-2 text-sm text-zinc-400">
                      <Check className="w-4 h-4 text-teal-400 flex-shrink-0" />
                      {feature}
                    </li>
                  ))}
                </ul>

                <div className="flex gap-3">
                  <Link to={`/shop/${machine.id}`} className="flex-1">
                    <Button className="w-full h-12 bg-teal-400 text-black font-bold uppercase tracking-wider hover:bg-teal-300">
                      Shop Now
                      <ArrowRight className="ml-2 w-4 h-4" />
                    </Button>
                  </Link>
                  <Link to={`/shop/${machine.id}`}>
                    <Button variant="outline" className="h-12 px-4 border-zinc-700 hover:bg-zinc-800">
                      Details
                    </Button>
                  </Link>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Benefits */}
        <div className="grid md:grid-cols-3 gap-8 mb-16">
          <div className="text-center p-8 bg-[#121416] border border-zinc-800 rounded-sm">
            <Truck className="w-10 h-10 text-teal-400 mx-auto mb-4" />
            <h3 className="font-bold mb-2">Free Shipping</h3>
            <p className="text-zinc-500 text-sm">On all machine orders. Delivered in 5-7 business days.</p>
          </div>
          <div className="text-center p-8 bg-[#121416] border border-zinc-800 rounded-sm">
            <Shield className="w-10 h-10 text-teal-400 mx-auto mb-4" />
            <h3 className="font-bold mb-2">Warranty Protection</h3>
            <p className="text-zinc-500 text-sm">1-2 year manufacturer warranty on all machines.</p>
          </div>
          <div className="text-center p-8 bg-[#121416] border border-zinc-800 rounded-sm">
            <CreditCard className="w-10 h-10 text-teal-400 mx-auto mb-4" />
            <h3 className="font-bold mb-2">Flexible Financing</h3>
            <p className="text-zinc-500 text-sm">Pay over time with Affirm. 0% APR available.</p>
          </div>
        </div>

        {/* Classes CTA */}
        <div className="bg-teal-400/10 border border-teal-400/30 rounded-sm p-12 text-center">
          <Play className="w-12 h-12 text-teal-400 mx-auto mb-4" />
          <h3 className="text-2xl font-bold mb-4">Already Have a Machine?</h3>
          <p className="text-zinc-400 mb-6 max-w-xl mx-auto">
            Subscribe to Lagree MVMT for unlimited access to live and on-demand classes taught by certified instructors.
          </p>
          <Link to="/register">
            <Button className="h-12 px-8 bg-teal-400 text-black font-bold uppercase tracking-wider hover:bg-teal-300">
              Start 7-Day Free Trial
              <ArrowRight className="ml-2 w-4 h-4" />
            </Button>
          </Link>
        </div>
      </main>

      {/* Footer */}
      <footer className="py-12 px-6 border-t border-zinc-800 mt-16">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <Logo linkTo="/" size="small" />
            <div className="flex items-center gap-6 text-zinc-500 text-sm">
              <a href="#" className="hover:text-white transition-colors">Shipping & Returns</a>
              <a href="#" className="hover:text-white transition-colors">Warranty</a>
              <a href="#" className="hover:text-white transition-colors">Contact</a>
            </div>
            <div className="text-zinc-600 text-sm">
              © 2026 Lagree MVMT. All rights reserved.
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default ShopPage;
